import React from 'react';
import { motion } from 'framer-motion';
import { Rocket, ArrowLeft, Mail, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/auth-context';
import { apiFetch } from '@/lib/api';

const ComingSoon: React.FC = () => {
  const { user } = useAuth();
  const [isWishlisted, setIsWishlisted] = React.useState(false);

  React.useEffect(() => {
    const saved = localStorage.getItem('fmg_wishlist');
    if (saved) {
      const list = JSON.parse(saved);
      if (list.includes('Creator')) {
        setIsWishlisted(true);
      }
    }
  }, []);

  const handleWishlist = async () => {
    const saved = localStorage.getItem('fmg_wishlist');
    const list = saved ? JSON.parse(saved) : [];
    if (!list.includes('Creator')) {
      list.push('Creator');
      localStorage.setItem('fmg_wishlist', JSON.stringify(list));
      setIsWishlisted(true);

      // Sync to backend if logged in
      if (user) {
        try {
          await apiFetch('/api/subscribe', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email: user.email,
              name: user.username,
              plan: 'Creator'
            })
          });
        } catch (err) {
          console.error('Failed to sync wishlist:', err);
        }
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4">
      <div className="max-w-2xl w-full text-center">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="mb-8 inline-block p-6 bg-black/5 rounded-full border border-black/5"
        >
          <Rocket className="w-16 h-16 text-black animate-bounce" />
        </motion.div>

        <motion.h1
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-5xl md:text-7xl font-black text-black mb-6 tracking-tighter uppercase font-display leading-tight"
        >
          Coming <span className="text-zinc-200">Soon</span>
        </motion.h1>

        <motion.p
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-xl text-zinc-600 mb-12 leading-relaxed font-medium"
        >
          {isWishlisted 
            ? "You're on the list! We've added you to the Level Editor waitlist. We'll notify you as soon as it's ready."
            : "We are currently working hard on the Level Editor. This feature will be available in a future update. Stay tuned!"}
        </motion.p>

        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Link
            to="/"
            className="flex items-center gap-2 px-8 py-4 bg-zinc-50 border border-black/5 text-black font-bold rounded-2xl hover:bg-zinc-100 transition-all font-sans uppercase text-[10px] tracking-widest"
          >
            <ArrowLeft className="w-5 h-5" /> Back to Home
          </Link>
          <button
            onClick={handleWishlist}
            disabled={isWishlisted}
            className={`flex items-center gap-2 px-8 py-4 font-bold rounded-2xl transition-all shadow-xl uppercase text-[10px] tracking-widest ${
              isWishlisted 
                ? 'bg-emerald-50 text-emerald-600 border border-emerald-100 cursor-default' 
                : 'bg-black text-white hover:bg-zinc-800 shadow-black/10'
            }`}
          >
            {isWishlisted ? (
              <>
                <Mail className="w-5 h-5" /> Already Wishlisted
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" /> Wishlist Now
              </>
            )}
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default ComingSoon;
